import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AppWindow, BookOpen, ExternalLink, Hash, Laptop, Search, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { formatDate, formatRelative } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import {
  knowledgeArticleHref,
  knowledgeListHref,
  useArticlesFor,
  VISIBILITY_LABEL,
  VISIBILITY_OPTIONS,
  VISIBILITY_TONE,
  type KnowledgeArticle,
  type KnowledgeArticleSummary,
  type KnowledgePage,
  type KnowledgeSubject,
  type KnowledgeTagCounts,
  type KnowledgeVisibility,
} from '../lib/knowledge';
import { ModelImage } from './model-image';
import { RichTextView } from './rich-text';
import { Badge, Card, CardHeader, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner } from './ui';

const PAGE_SIZE = 20;
const FILTERS = { search: '', modelId: '', softwareId: '', topic: '', visibility: '', mine: '', page: '1' };
export type KnowledgeFilters = typeof FILTERS;

/** Who can read an article, one badge each. */
export function VisibilityBadges({ visibility }: { visibility: KnowledgeVisibility[] }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {visibility.map((value) => (
        <Badge key={value} tone={VISIBILITY_TONE[value]}>
          {VISIBILITY_LABEL[value]}
        </Badge>
      ))}
    </span>
  );
}

/**
 * Help articles about the device or software someone picked on a form, so an easy fix can be tried before a ticket is
 * opened. Nothing shows until something is picked that has articles tagged with it. Every link opens in its own tab, so
 * a half-written form isn't lost, and the heading names what the articles are about.
 */
export function RelatedArticles({ label, ...subject }: KnowledgeSubject & { label?: string | null }) {
  const list = useArticlesFor(subject);
  const articles = list.data?.items ?? [];
  if (articles.length === 0) return null;
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50/70 p-3" aria-label="Help articles">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <h3 className="inline-flex min-w-0 items-center gap-1.5 text-sm font-medium text-slate-900">
          <BookOpen className="size-4 shrink-0 text-slate-400" />
          <span className="truncate">{label ? `Help articles for ${label}` : 'Help articles'}</span>
        </h3>
        <a
          href={knowledgeListHref(subject)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-brand-700 hover:underline"
        >
          View all KB articles <ExternalLink className="size-3" />
        </a>
      </div>
      <ul className="mt-1.5">
        {articles.map((article) => (
          <li key={article.id}>
            <a href={knowledgeArticleHref(article.id)} target="_blank" rel="noreferrer" className="group flex items-start gap-1.5 rounded-md px-2 py-1.5 hover:bg-white">
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-slate-800 group-hover:text-brand-800">{article.title}</span>
                {article.excerpt && <span className="mt-0.5 block truncate text-xs text-slate-500">{article.excerpt}</span>}
              </span>
              <ExternalLink className="mt-1 size-3 shrink-0 text-slate-400" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

function TagChip({ icon: Icon, children, to }: { icon: typeof Hash; children: ReactNode; to?: string }) {
  const className = 'inline-flex max-w-full items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600';
  const content = (
    <>
      <Icon className="size-3 shrink-0 text-slate-400" />
      <span className="truncate">{children}</span>
    </>
  );
  return to ? (
    <Link to={to} className={`${className} hover:bg-brand-50 hover:text-brand-800`}>
      {content}
    </Link>
  ) : (
    <span className={className}>{content}</span>
  );
}

/** Search, filters, and the list of articles, for the signed-in knowledgebase and the public one. */
export function KnowledgeBrowser({
  queryKey,
  fetchList,
  fetchTags,
  articleHref,
  staff = false,
  empty,
}: {
  queryKey: unknown[];
  fetchList(filters: KnowledgeFilters & { pageSize: number }): Promise<KnowledgePage>;
  fetchTags(): Promise<KnowledgeTagCounts>;
  articleHref(id: string): string;
  /** The technology team also filters by who can read an article, and sees that on each one. */
  staff?: boolean;
  /** Shown when there are no articles at all. */
  empty?: ReactNode;
}) {
  const [filters, setFilters] = useUrlState(FILTERS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput.trim(), 300);
  const page = Number(filters.page) || 1;

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const tags = useQuery({ queryKey: [...queryKey, 'tags'], queryFn: fetchTags, staleTime: 60_000 });
  const list = useQuery({
    queryKey: [...queryKey, 'list', filters],
    queryFn: () => fetchList({ ...filters, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  const filtered = !!(filters.search || filters.modelId || filters.softwareId || filters.topic || filters.visibility || filters.mine);
  const clear = () => {
    setSearchInput('');
    setFilters({ search: '', modelId: '', softwareId: '', topic: '', visibility: '', mine: '' });
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
        <div className="relative w-full sm:w-80">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
          <Input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search articles" aria-label="Search articles" className="pl-9" />
        </div>
        {!!tags.data?.models.length && (
          <Select value={filters.modelId} onChange={(event) => setFilters({ modelId: event.target.value })} className="w-auto max-w-full" aria-label="Device">
            <option value="">Any device</option>
            {tags.data.models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name} ({model.count})
              </option>
            ))}
          </Select>
        )}
        {!!tags.data?.software.length && (
          <Select value={filters.softwareId} onChange={(event) => setFilters({ softwareId: event.target.value })} className="w-auto max-w-full" aria-label="Software">
            <option value="">Any software</option>
            {tags.data.software.map((tag) => (
              <option key={tag.id} value={tag.id}>
                {tag.name} ({tag.count})
              </option>
            ))}
          </Select>
        )}
        {!!tags.data?.topics.length && (
          <Select value={filters.topic} onChange={(event) => setFilters({ topic: event.target.value })} className="w-auto max-w-full" aria-label="Topic">
            <option value="">Any topic</option>
            {tags.data.topics.map((tag) => (
              <option key={tag.value} value={tag.value}>
                {tag.value} ({tag.count})
              </option>
            ))}
          </Select>
        )}
        {staff && (
          <>
            <Select value={filters.visibility} onChange={(event) => setFilters({ visibility: event.target.value })} className="w-auto" aria-label="Who can read it">
              <option value="">Anyone who can read it</option>
              {VISIBILITY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <label className="inline-flex items-center gap-2 px-1 text-sm text-slate-600">
              <input type="checkbox" checked={filters.mine === 'true'} onChange={(event) => setFilters({ mine: event.target.checked ? 'true' : '' })} className="size-4 rounded border-slate-300 accent-brand-700" />
              Written by me
            </label>
          </>
        )}
        {filtered && (
          <button type="button" onClick={clear} className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <X className="size-3" /> Clear
          </button>
        )}
        {list.isFetching && !list.isLoading && <Spinner className="size-4" />}
      </div>

      {list.isLoading ? (
        <div className="flex h-60 items-center justify-center">
          <Spinner />
        </div>
      ) : list.error || !list.data ? (
        <ErrorState error={list.error} onRetry={() => list.refetch()} />
      ) : list.data.items.length === 0 ? (
        filtered ? (
          <EmptyState icon={<Search />} title="No articles match" description="Try other words, or clear the filters." />
        ) : (
          (empty ?? <EmptyState icon={<BookOpen />} title="No articles yet" />)
        )
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {list.data.items.map((article) => (
              <ArticleRow key={article.id} article={article} href={articleHref(article.id)} staff={staff} />
            ))}
          </ul>
          <Pagination page={page} pageSize={PAGE_SIZE} total={list.data.total} onPage={(next) => setFilters({ page: String(next) }, { resetPage: false })} />
        </>
      )}
    </Card>
  );
}

function ArticleRow({ article, href, staff }: { article: KnowledgeArticleSummary; href: string; staff: boolean }) {
  const tagged = article.models.length + article.software.length + article.topics.length > 0;
  return (
    <li>
      <Link to={href} className="flex flex-col gap-2 px-5 py-4 hover:bg-slate-50/80 sm:flex-row sm:items-start sm:gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-slate-900">{article.title}</p>
          {article.excerpt && <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{article.excerpt}</p>}
          {tagged && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {article.models.map((model) => (
                <TagChip key={model.id} icon={Laptop}>
                  {model.name}
                </TagChip>
              ))}
              {article.software.map((tag) => (
                <TagChip key={`s:${tag.id}`} icon={AppWindow}>
                  {tag.name}
                </TagChip>
              ))}
              {article.topics.map((tag) => (
                <TagChip key={`t:${tag}`} icon={Hash}>
                  {tag}
                </TagChip>
              ))}
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs text-slate-400 sm:flex-col sm:items-end">
          {staff && <VisibilityBadges visibility={article.visibility} />}
          <span>Updated {formatRelative(article.updatedAt)}</span>
        </div>
      </Link>
    </li>
  );
}

/** An article to read, with what it's tagged with beside it. Tags link back to the list, filtered. */
export function KnowledgeArticleView({
  article,
  imageSrc,
  listPath,
  staff = false,
  actions,
}: {
  article: KnowledgeArticle;
  imageSrc(imageId: string): string;
  /** The knowledgebase list this article belongs to, e.g. /knowledge. */
  listPath: string;
  staff?: boolean;
  actions?: ReactNode;
}) {
  const filterHref = (key: keyof KnowledgeFilters, value: string) => `${listPath}?${new URLSearchParams({ [key]: value })}`;
  const tagged = article.models.length + article.software.length + article.topics.length > 0;

  return (
    <>
      <PageHeader
        back={{ to: listPath, label: 'Knowledgebase' }}
        title={article.title}
        description={article.summary ?? undefined}
        meta={
          <>
            {staff && <VisibilityBadges visibility={article.visibility} />}
            <span className="text-xs text-slate-500">
              Updated {formatDate(article.updatedAt)}
              {article.updatedByName && ` by ${article.updatedByName}`}
            </span>
          </>
        }
        actions={actions}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_17rem]">
        <Card className="min-w-0 px-5 py-6 sm:px-8 sm:py-8">
          {article.body.blocks.length > 0 ? (
            <RichTextView doc={article.body} imageSrc={imageSrc} />
          ) : (
            <p className="text-sm text-slate-500">This article doesn’t have anything in it yet.</p>
          )}
        </Card>

        <Card>
          <CardHeader title="About this article" />
          <div className="space-y-4 p-5 text-sm">
            {article.models.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">Devices</h3>
                <ul className="space-y-2">
                  {article.models.map((model) => (
                    <li key={model.id}>
                      <Link to={filterHref('modelId', model.id)} className="flex items-center gap-2.5 text-slate-800 hover:text-brand-700">
                        <ModelImage url={model.imageUrl} alt="" className="size-9" />
                        <span className="min-w-0">{model.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {article.software.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">Software</h3>
                <div className="flex flex-wrap gap-1.5">
                  {article.software.map((tag) => (
                    <TagChip key={tag.id} icon={AppWindow} to={filterHref('softwareId', tag.id)}>
                      {tag.name}
                    </TagChip>
                  ))}
                </div>
              </section>
            )}
            {article.topics.length > 0 && (
              <section>
                <h3 className="mb-2 text-xs font-medium tracking-wide text-slate-500 uppercase">Topics</h3>
                <div className="flex flex-wrap gap-1.5">
                  {article.topics.map((tag) => (
                    <TagChip key={tag} icon={Hash} to={filterHref('topic', tag)}>
                      {tag}
                    </TagChip>
                  ))}
                </div>
              </section>
            )}
            {!tagged && <p className="text-slate-500">Not tagged with a device, software, or topic.</p>}
            {article.authorName && (
              <p className="border-t border-slate-100 pt-4 text-xs text-slate-500">
                Written by {article.authorName} on {formatDate(article.createdAt)}
              </p>
            )}
          </div>
        </Card>
      </div>
    </>
  );
}
